import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, Input } from 'ALIASED-antd';
import RandoxAutoCompleteSearchInput from './RandoxAutoCompleteSearchInput';
import styled from '@emotion/styled';

const AddressContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormContainer = styled.div`
  .randox-address-form {
    .autoSuggestInput,
    .address-input {
      max-height: unset;
      min-height: 48px;
      font-weight: 400;
      font-size: 16px;
    }

    .ant-form-item-label {
      font-weight: bold;
      font-size: 16px;
    }

    .ant-input-suffix {
      background-color: var(--productPrimary);
      color: #fff;
      border-radius: 24px;

      .search_btn {
        padding: 0 12px !important;
        top: 19px;
        font-weight: bold;
        font-size: 16px;
      }
    }

    .react-autosuggest__suggestions-container {
      display: none;
      position: absolute;
      top: 51px;
      width: 100%;
      border: 1px solid #aaa;
      background-color: #fff;
      font-weight: 300;
      font-size: 16px;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      max-height: 400px;
      overflow-y: auto;
      z-index: 2;

      &--open {
        display: block;
      }
    }

    .react-autosuggest__suggestions-list {
      margin: 0;
      padding: 0;
      list-style-type: none;

      .react-autosuggest__suggestion {
        cursor: pointer;
        padding: 10px 20px;
      }
    }
  }
`;

export interface Address {
  addressLine1?: string;
  addressLine2?: string;
  town?: string;
  country?: string;
  postcode?: string;
}

interface PostcodeResponse {
  line1?: string;
  line2?: string;
  line3?: string;
  region?: string;
  town?: string;
  postcode?: string;
  country?: string;
  isAddressFound: boolean;
}

const RandoxHomeTestAddressField = ({ onAddressChange }: { onAddressChange: (address: Address) => void }) => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const [showAddressFields, setShowAddressFields] = useState(false);

  const expandAddressFields = () => setShowAddressFields(true);

  const handleAddressFound = (address: PostcodeResponse) => {
    const normalizedAddress = {
      addressLine1: address.line1,
      addressLine2: address.line2,
      town: address.town,
      country: address.country,
      postcode: address.postcode
    };

    form.setFieldsValue({ ...normalizedAddress, postcodeDisplay: address.postcode });
    onAddressChange(normalizedAddress);
    setShowAddressFields(true);
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const address: Address = {
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      town: values.town,
      country: values.country,
      postcode: values.postcodeDisplay
    };
    onAddressChange(address);
  };

  return (
    <FormContainer>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: '100vw' }}
        onValuesChange={handleFormChange}
        onSubmitCapture={(e) => e.preventDefault()}
        className="randox-address-form"
      >
        <RandoxAutoCompleteSearchInput onAddressFound={handleAddressFound} />

        {!showAddressFields && (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={expandAddressFields}
            data-testid="enter-address-manually-button"
          >
            {t('Enter address manually')}
          </Button>
        )}

        {showAddressFields && (
          <AddressContainer>
            <Form.Item
              label={t('Postcode')}
              name="postcodeDisplay"
              rules={[{ required: true, message: 'Postcode is required' }]}
            >
              <Input placeholder={t('Postcode')} className="address-input" />
            </Form.Item>
            <Form.Item
              label={t('Address Line 1')}
              name="addressLine1"
              rules={[{ required: true, message: 'Address Line 1 is required' }]}
            >
              <Input placeholder={t('Address Line 1')} className="address-input" />
            </Form.Item>
            <Form.Item label={t('Address Line 2')} name="addressLine2">
              <Input placeholder={t('Address Line 2')} className="address-input" />
            </Form.Item>
            <Form.Item label={t('Town/City')} name="town">
              <Input placeholder={t('Town/City')} className="address-input" />
            </Form.Item>
            <Form.Item label={t('Country')} name="country">
              <Input placeholder={t('Country')} className="address-input" />
            </Form.Item>
          </AddressContainer>
        )}
      </Form>
    </FormContainer>
  );
};

export default RandoxHomeTestAddressField;
